import {defineStore} from 'pinia'
import axios from "axios";
import {useAppStore} from "@/stores/app";
import {useToast} from "vue-toastification";
import {useUserStore} from "@/stores/user";
import {useMessageTemplates} from "@/stores/message-templates/store";
import {useMailServerStore} from "@/stores/mail-server";
import {handleAuthError} from "@/stores/includes/interceptors";
import {
    loadTokenExpiryFromStorage,
    // loadTokenFromStorage,
    // updateToken,
    updateTokenExpiry
} from "@/stores/includes/oauth";
import {resetCookieValue} from "@/stores/includes/cookies";
import {
    getBoolFromStorage,
    getStringFromStorage,
    setBoolToStorage, setStringToStorage,
} from "@/stores/includes/storage";
import {toJSON} from "lodash/seq";

const toast = useToast();

export const sessionsCookieName = 'ts_session'
export const localStorageRefreshingFlag = 'ts_refreshing'
export const localStorageAuthExpiresKey = 'ts_auth_expires_at'
export const localStorageImposterDataKey = 'ts_auth_imposter_data'

// TODO: Change back to 5 minutes again
//export const refreshBeforeMilliseconds = 5 * 60 * 1000 // 5 minutes
export const refreshBeforeMilliseconds = 12 * 1000 // 12 seconds

const getExpireAt = (ttl) => new Date((new Date).getTime() + ttl * 1000).getTime()

export const useAuthStore = defineStore('auth', {
    state: () => ({
        expiresAt : null,
        imposterName: null,
        imposterToken: null,
        refreshFunctionId: null,
    }),
    getters: {
        // We need to set that in local storage, so we can redirect the user, event when the store is not booted yet
        authenticated: (state) => (state.expiresAt ?? getBoolFromStorage(localStorageAuthExpiresKey)) > Date.now(),
        isImposter: (state) => state.imposterName !== null,
    },
    actions: {
        /**
         * Initializes the authentication system
         *
         * @returns {Promise<void>}
         */
        async init() {

            // Add interceptor for auth token refreshing
            axios.interceptors.response.use(
                (response) => response,
                (error) => handleAuthError(error)
            )

            // Listen on local storage events
            window.addEventListener('storage', (event) => {
                // TODO: We can add here a synchronized login/logout feature on all tabs
                if (event.key === localStorageAuthExpiresKey) {
                    // We can savely do that here, since localStorage events fired from our own requests, are not
                    // firing local events, so we do not make a recursive function...
                    updateTokenExpiry(getStringFromStorage(localStorageAuthExpiresKey))
                }
            });

            // Initially set to false, to avoid lock up
            setBoolToStorage(localStorageRefreshingFlag, false)

            // loadTokenFromStorage()
            loadTokenExpiryFromStorage()

            this.loadImposterDataFromLocalStorage()
        },
        /**
         * Tries a login
         *
         * @param email
         * @param password
         * @returns {Promise<boolean>}
         */
        async login(email, password) {
            return await this.authenticate({
                email: email,
                password: password,
            })
        },
        async loginWithToken(token) {
            // When alredy authenticated, we first need to logout (in the background parallel) so we have a clean state
            if (this.authenticated) this.logout().then();
            return await this.authenticate({
                token: token,
            })
        },
        async authenticate(authData) {
            try {
                const { data : { expires_in, imposter } } = await axios.post('/api/auth/login', authData);

                // Update state
                updateTokenExpiry(getExpireAt(expires_in))

                // If we have an imposter logged in update state
                if (imposter) {
                    // Set name
                    this.imposterName = imposter.name
                    this.imposterToken = authData.token
                    // Start refresh mechanism
                    this.startImposterTokenRefreshLoop(imposter.ttl)
                    // Save to local storage to be refresh proof
                    setStringToStorage(localStorageImposterDataKey, JSON.stringify({
                        ...imposter,
                        token: authData.token,
                    }))
                }

                // Boot the application
                const app = useAppStore()

                return await app.boot()
            } catch (e) {
                console.error("Error during login")
                console.error(e)

                switch(e.response?.data?.code) {
                    case 'unauthorized':
                        toast.error("Fehler beim Login. Prüfe Benutzername und Passwort!");
                        break;
                    case 'team_account_closed':
                        toast.error("Dieser Account wurde durch das Auflösen eines Teams geschlossen!");
                        break;
                    case 'account_deactivated_temporarily':
                        toast.error("Dieser Account ist vorübergehend deaktiviert!");
                        break;
                    case 'account_deactivated_permanently':
                        toast.error("Dieser Account ist dauerhaft deaktiviert!");
                        break;
                    default:
                        toast.error("Fehler beim Login. Bitte versuche es später erneut.");
                }

                return false;
            }
        },
        startImposterTokenRefreshLoop(ttl) {
            setTimeout(this.refreshImposterToken, ttl * 1000)
        },
        async refreshImposterToken(){
            // If we are not imposter, break recursive loop (i.E. user switched and the callback is not cancelled)
            if (!this.isImposter) return;
            try {
                const { data : { ttl } } = await axios.post('/api/auth/imposter/refresh', {
                    'token' : this.imposterToken
                });

                this.startImposterTokenRefreshLoop(ttl)
            } catch (error) {
                // The team member revoked the imposter login consent in their legal settings (TLS-249).
                // The token survives on purpose, so we can jump straight back into the admin account.
                if (error.response?.status === 409) {
                    toast.error("Der Zugriff auf diesen Account wurde widerrufen. Du wirst in deinen Account zurückgewechselt.")
                    return await this.jumpBackAccount()
                }

                toast.error("Fehler beim Aktualisieren der Sitzung. Nach Logout musst du dich mit deinem Account wieder anmelden!")
                console.error("Error during imposter token refresh")
                console.error(error)
            }
        },
        loadImposterDataFromLocalStorage() {

            // EDGE CASE: When we have a token set in the URL we MUST not load from the local storage, to avoid when refreshing
            //            the token (this.refreshImposterToken()) we have the old token from the storage and therefore the backend would delete our cookie
            //            and we can't login with the new token...
            // Note from Julian: I know, you should not do URL frontend stuff in a store, but since we boot this store, before the token validation logic kicks in
            //                   we have a special excuse here... There are a few edge cases where this happens, but it would be too much work to handle all edge cases, this is a better trade off fix
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('token')) return;


            const data = JSON.parse(getStringFromStorage(localStorageImposterDataKey) ?? '{}')

            // Verify data
            if (!data.token) return this.deleteImposterDataFromLocalStorage();
            if (!data.name) return this.deleteImposterDataFromLocalStorage();
            if (!data.ttl) return this.deleteImposterDataFromLocalStorage();

            this.imposterName = data.name
            this.imposterToken = data.token

            // Start refresh mechanism => Directly refresh, since we do not know, how long the TTL is already to its EOL
            this.refreshImposterToken()

            console.log(data)
        },
        deleteImposterDataFromLocalStorage() {
              setStringToStorage(localStorageImposterDataKey, null)
        },
        /**
         * Refreshes the token
         *
         * @returns {Promise<void>}
         */
        async refresh(resetClaims = false) {

            console.info("Refreshing the auth token")

            if (this.getRefreshStatus()) {
                console.info("Currently another tab is refreshing, skipping to avoid race conditions")
                return
            }

            // Flag to refresh
            setBoolToStorage(localStorageRefreshingFlag, true)

            try {
                const { data : { expires_in } } = await axios.post('/api/auth/refresh',
                    resetClaims ? { refresh_claims: true } : {}
                );

                // Update state
                updateTokenExpiry(getExpireAt(expires_in))

                // Update flag
                setBoolToStorage(localStorageRefreshingFlag, false)

            } catch (e) {
                console.error("Error during refresh")
                console.error(e)
                toast.error("Sitzung abgelaufen. Bitte melde dich erneut an!")

                // Log the user out
                await this.reset()

                // Update flag
                setBoolToStorage(localStorageRefreshingFlag, false)

                // Go back to login
                window.location.href = "/login"

            }
        },
        getRefreshStatus() {
            return getBoolFromStorage(localStorageRefreshingFlag)
        },
        /**
         * Switch to account
         */
        async switchAccount(userId) {
            try {
                const { data : { token } } = await axios.post(`/api/auth/imposter/${userId}`);

                // First logout current user (do not need to wait for it, since we are directly jumping to the next session)
                await this.logout()

                // Magic shit injected in main.js#86 => store.router = markRaw(router)
                this.router.replace('/login?token=' + token)
            } catch (e) {
                console.error("Error during switch account")
                console.error(e)
                toast.error("Fehler beim Wechseln des Accounts")
            }
        },
        async jumpBackAccount() {
            console.log("Jumping back to account")

            // Save token before logout => Logout will reset this store...
            const token = this.imposterToken

            await this.logout()

            this.router.replace('/login?token=' + token)
        },
        /**
         * Logs out the current user
         *
         * @returns {Promise<boolean>}
         */
        async logout() {
            try {

                // Revoke bearer token
                // We do not call it via await to fasten the logout process -> Session invalidation on server side is not need to be waited for
                axios.post('/api/auth/logout').then() // The then is only there, so that my autism doesn't trigger when PhpStorm says I need to await for an async call

                // Reset state
                await this.reset()

                return true
            } catch (e) {
                console.error("Error during logout")
                console.error(e)
                toast.error("Fehler beim Logout")
                return false
            }
        },
        async updatePassword(password, passwordConfirmation) {
            try {
                await axios.post(`/api/auth/password`, {
                    'password' : password,
                    'password_confirmation' : passwordConfirmation
                })
                toast.success("Passwort aktualisiert")

                await this.reset()

                return true
            } catch (error) {
                toast.error("Leider ist das aktualisieren des Passworts fehlgeschlagen")
                console.error(error)
            }
            return false
        },
        /**
         * Resets the auth state
         *
         * @returns {Promise<void>}
         */
        async reset() {

            console.info("Resetting auth state")

            // Reset session cookie
            resetCookieValue(sessionsCookieName)

            // Update state
            updateTokenExpiry(null)

            // Reset refreshing flag (to be sure)
            setBoolToStorage(localStorageRefreshingFlag, false)

            // Reset imposter data
            this.deleteImposterDataFromLocalStorage()
            this.imposterName = null
            this.imposterToken = null

            // Also reset user store
            const user = useUserStore();
            user.reset();

            // Reset message templates store
            const messageTemplates = useMessageTemplates();
            messageTemplates.reset();

            // Reset mail server store, so the next account does not see this account's server
            const mailServer = useMailServerStore();
            mailServer.reset();

            // Reboot the app
            const app = useAppStore()
            await app.boot()
        },
        async requestResetPassword(email){
            try {
                await axios.post('api/auth/password/email', {
                    'email' : email
                })
                return true
            } catch (error) {
                if (error.response.status !== 400) {
                    toast.error("Fehler beim anfragen des Reset Links")
                    return null
                }

                if (error.response.data.status === 'passwords.throttled') toast.error("Zu viele Anfragen")
                if (error.response.data.status === 'passwords.user') toast.error("Diese Mail existiert nicht")

                return error.response.data.status
            }
        },
        async resetPassword(email, password, passwordConfirmation, token){
            try {
                await axios.post('api/auth/password/reset', {
                    'email' : email,
                    'password' : password,
                    'password_confirmation' : passwordConfirmation,
                    'token' : token
                })
                return true
            } catch (error) {
                if (error.response.status !== 400) {
                    toast.error("Fehler beim setzen des Passwortes")
                    return null
                }

                if (error.response.data.status === 'passwords.token') toast.error("Ungültiger Reset-Link")
                if (error.response.data.status === 'passwords.user') toast.error("Diese Mail stimmt nicht überein")

                return error.response.data.status
            }
        }
    }
})